/**
 * usePdfOptionsDialog — PDF-6.9b PDF Options sub-dialog orchestration hook.
 *
 * AutoCAD parity: the Page Setup dialog has a "PDF Options" sub-modal that
 * captures PDF-specific knobs (vector quality / font handling / layer
 * inclusion / bookmarks). The user clicks "PDF Options..." in the Page Setup
 * Manager footer, edits a draft, then OK commits it (Cancel discards). The
 * committed value is read NON-REACTIVELY at print-time by the actual PDF
 * pipeline:
 *
 *   - {@link usePrintDialog} `onConfirm` → `exportLayoutPdf({ omitLayers,
 *     toleranceMm: dpiToToleranceMm(vectorDpi) })`
 *   - {@link usePublishLayouts} `publishSelected` → `publishLayouts(...,
 *     { omitLayers, includeBookmarks, toleranceMm })`
 *
 * Returned shape mirrors the other dialog hooks (`usePrintDialog`,
 * `usePageSetupDialog`):
 *
 *   {
 *     isOpen,
 *     config,        // committed; the print-time source of truth
 *     getConfig,     // non-reactive read for hooks that don't want a re-render
 *     props: {
 *       draft,       // in-flight edits (the dialog binds to this)
 *       availableLayers,  // layer NAMES from styleTables.layers
 *       onChange,    // partial-merge update to the draft
 *       onConfirm,   // OK: commit draft → config, close
 *       onCancel,    // Cancel: discard draft, close
 *     },
 *     open,          // copy config → draft, open
 *     close,         // imperative close (used by resetShellState)
 *   }
 *
 * # In-flight draft model
 *
 * Two PdfExportConfig values are held: `config` (the committed value other
 * hooks read) and `draft` (the in-flight edits). `open()` seeds draft from
 * config (so re-opening starts from the last committed state, not the
 * defaults). `onConfirm` commits draft → config, then closes. `onCancel`
 * closes without touching config. This mirrors how AutoCAD's PDF Options
 * sub-modal handles OK/Cancel.
 *
 * # `getConfig()` — non-reactive read
 *
 * Print-time callers (`usePrintDialog.onConfirm`, the SpaceTabsBar PUBLISH
 * action) need the LATEST committed config at the moment the user clicked
 * Print / Publish — NOT a reactive subscription that would rebuild the
 * dialog. The host captures `getConfig` once in a ref and reads through it.
 * Defaults to {@link defaultPdfExportConfig} until the user opens & confirms
 * the dialog at least once.
 */

import { useCallback, useMemo, useRef, useState } from "react";

import {
  defaultPdfExportConfig,
  type PdfExportConfig,
} from "../print/pdf/pdfExportConfig";
import { useT } from "../i18n";
import { useDialogOpenState } from "./useDialogOpenState";

/**
 * Layer NAMES the dialog offers as include-checkboxes. Built by the host
 * from `doc.styleTables.layers` and re-seeded each `open()` call.
 */
export interface PdfOptionsLayerOption {
  /** Stored layer name (exact case, as it flows through OCG). */
  readonly name: string;
}

export interface UsePdfOptionsDialogParams {
  /** Receives localized open / confirm / cancel feed entries. */
  appendCommandFeed: (text: string) => void;
  /**
   * Resolve the current document's layer list (NON-REACTIVE — called inside
   * `open()` to snapshot the layers at the moment the dialog opens). The host
   * wires this to `useEditorStore.getState().geometryDocument.styleTables?.layers`.
   */
  resolveLayers: () => ReadonlyArray<PdfOptionsLayerOption>;
}

export interface UsePdfOptionsDialogProps {
  readonly draft: PdfExportConfig;
  readonly availableLayers: ReadonlyArray<PdfOptionsLayerOption>;
  /**
   * Partial merge into the in-flight draft. The dialog passes the fields it
   * actually edits (e.g. `{ vectorDpi: 2400 }`) and the hook merges them with
   * the prior draft, preserving the other fields.
   */
  readonly onChange: (patch: Partial<PdfExportConfig>) => void;
  /** OK button. Commits draft → config, closes the dialog. */
  readonly onConfirm: () => void;
  /** Cancel button. Closes without writing draft → config. */
  readonly onCancel: () => void;
}

export interface UsePdfOptionsDialogResult {
  isOpen: boolean;
  /** The COMMITTED config (the print-time source of truth, reactive). */
  config: PdfExportConfig;
  /**
   * Non-reactive read of the committed config — for hooks (`usePrintDialog`,
   * the PUBLISH path) that want the latest value at click time WITHOUT
   * re-rendering when it changes.
   */
  getConfig: () => PdfExportConfig;
  props: UsePdfOptionsDialogProps;
  /** Imperative open — used by the PageSetup footer "PDF Options..." button. */
  open: () => void;
  /** Imperative close — used by `resetShellState()`. */
  close: () => void;
}

export function usePdfOptionsDialog(
  params: UsePdfOptionsDialogParams,
): UsePdfOptionsDialogResult {
  const { appendCommandFeed, resolveLayers } = params;
  const t = useT();
  const { isOpen, open: openDialog, close: closeDialog } = useDialogOpenState();

  // Committed config — what print-time callers read. Seeded with the canonical
  // AutoCAD defaults until the user opens the dialog and confirms.
  const [config, setConfig] = useState<PdfExportConfig>(() =>
    defaultPdfExportConfig(),
  );
  // In-flight draft — the dialog edits this.
  const [draft, setDraft] = useState<PdfExportConfig>(() =>
    defaultPdfExportConfig(),
  );
  // Layer list snapshot — re-seeded each open() so newly-added layers appear
  // and dropped ones disappear.
  const [availableLayers, setAvailableLayers] = useState<
    ReadonlyArray<PdfOptionsLayerOption>
  >(() => []);

  // Ref mirror of `config` so `getConfig` can return the latest value without
  // re-rendering the dialog or the host (matches the `usePrintDialog` pattern
  // for `printPreviewWorldBounds`-style refs in other hooks).
  const configRef = useRef<PdfExportConfig>(config);
  configRef.current = config;

  const open = useCallback(() => {
    // Snapshot the current layer list at the moment the dialog opens. The
    // dialog edits a frozen view of the document; re-opening picks up new
    // layers and drops removed ones.
    const layers = resolveLayers();
    setAvailableLayers(layers);
    // Seed the draft from the LAST COMMITTED config (not the defaults), so
    // re-opening shows the user's last choices rather than resetting. We also
    // sanitize `omitLayers` to drop any names that no longer exist in the
    // layer table (a layer was deleted between sessions) — this keeps the
    // omit list a strict subset of the visible checkboxes.
    const layerNames = new Set(layers.map((l) => l.name));
    const sanitizedOmit = config.omitLayers.filter((n) => layerNames.has(n));
    setDraft({
      ...config,
      omitLayers: sanitizedOmit,
    });
    openDialog();
    appendCommandFeed(t("pdf_options.feed.opened"));
  }, [appendCommandFeed, config, openDialog, resolveLayers, t]);

  const close = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  const onChange = useCallback((patch: Partial<PdfExportConfig>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const onConfirm = useCallback(() => {
    // Commit draft → config. The ref mirror in `configRef` updates on the
    // next render (the `configRef.current = config` line above), but
    // print-time callers may invoke `getConfig()` immediately after the
    // synchronous `setConfig`; React batches state updates, so we copy the
    // draft into the ref directly here for the immediate read path.
    configRef.current = draft;
    setConfig(draft);
    closeDialog();
    appendCommandFeed(t("pdf_options.feed.confirmed"));
  }, [appendCommandFeed, closeDialog, draft, t]);

  const onCancel = useCallback(() => {
    closeDialog();
    appendCommandFeed(t("pdf_options.feed.cancelled"));
  }, [appendCommandFeed, closeDialog, t]);

  const getConfig = useCallback(() => configRef.current, []);

  const props = useMemo<UsePdfOptionsDialogProps>(
    () => ({
      draft,
      availableLayers,
      onChange,
      onConfirm,
      onCancel,
    }),
    [draft, availableLayers, onChange, onConfirm, onCancel],
  );

  return {
    isOpen,
    config,
    getConfig,
    props,
    open,
    close,
  };
}
